import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { api, type BenachrichtigungenAntwort } from '../api';
import { Icon } from './Icon';

// Liste und Einstellungen erst beim Öffnen laden – hält das Haupt-Bundle klein
const Liste = lazy(() => import('./Benachrichtigungen').then((m) => ({ default: m.BenachrichtigungenListe })));
const Gelesene = lazy(() => import('./Benachrichtigungen').then((m) => ({ default: m.GeleseneDialog })));
const Einstellungen = lazy(() => import('./Benachrichtigungen').then((m) => ({ default: m.EinstellungenDialog })));

const NACHLADEN_MS = 15_000;
/** Mehr liefert die API nicht auf einmal. */
const HOECHSTENS = 200;

/** Glocke in der Kopfleiste: Zahl der ungelesenen Benachrichtigungen, Klick öffnet die Liste der ungelesenen; die gelesenen stehen in einem eigenen Dialog. */
export function Glocke() {
  const [stand, setStand] = useState<BenachrichtigungenAntwort | null>(null);
  const [offen, setOffen] = useState(false);
  const [gelesene, setGelesene] = useState(false);
  const [einstellungen, setEinstellungen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const laden = useCallback(() => api.benachrichtigungen(HOECHSTENS, false).then(setStand).catch(() => { /* Glocke bleibt still, Kopfleiste nutzbar */ }), []);
  useEffect(() => {
    void laden();
    const t = window.setInterval(() => { if (!document.hidden) void laden(); }, NACHLADEN_MS);
    const fokus = () => void laden();
    window.addEventListener('focus', fokus);
    return () => { window.clearInterval(t); window.removeEventListener('focus', fokus); };
  }, [laden]);

  // Liste schließt bei Klick daneben und mit Esc (kein Dialog, sondern ein Aufklappmenü wie „Export“)
  useEffect(() => {
    if (!offen) return;
    const zu = (e: PointerEvent) => { if (!ref.current?.contains(e.target as Node)) setOffen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOffen(false); };
    document.addEventListener('pointerdown', zu);
    window.addEventListener('keydown', esc);
    return () => { document.removeEventListener('pointerdown', zu); window.removeEventListener('keydown', esc); };
  }, [offen]);

  const gelesen = async (ids: string[] | 'alle') => {
    const jetzt = new Date().toISOString();
    setStand((s) => s && {
      ungelesen: ids === 'alle' ? 0 : Math.max(0, s.ungelesen - s.eintraege.filter((b) => ids.includes(b.id) && !b.gelesenAm).length),
      eintraege: s.eintraege.map((b) => (!b.gelesenAm && (ids === 'alle' || ids.includes(b.id)) ? { ...b, gelesenAm: jetzt } : b)),
    });
    try {
      const r = await api.benachrichtigungenGelesen(ids);
      setStand((s) => s && { ...s, ungelesen: r.ungelesen });
    } catch { void laden(); }
  };

  const zahl = stand?.ungelesen ?? 0;
  return (
    <div className="menu glocke" ref={ref}>
      <button className={'btn icon' + (offen ? ' on' : '')} data-testid="glocke"
        title={zahl ? `Benachrichtigungen (${zahl} ungelesen)` : 'Benachrichtigungen'}
        onClick={() => { if (!offen) void laden(); setOffen(!offen); }}>
        <Icon name="bell" />
        {zahl > 0 && <span className="glocke-zahl" data-testid="glocke-zahl">{zahl > 99 ? '99+' : zahl}</span>}
      </button>
      {offen && (
        <div className="pop glocke-pop" data-testid="glocke-liste">
          <Suspense fallback={<div className="hint" style={{ padding: 12 }}>Wird geladen …</div>}>
            <Liste stand={stand} onGelesen={gelesen} onSchliessen={() => setOffen(false)}
              onGelesene={() => { setOffen(false); setGelesene(true); }}
              onEinstellungen={() => { setOffen(false); setEinstellungen(true); }} />
          </Suspense>
        </div>
      )}
      {/* Dialog außerhalb der Kopfleiste einhängen, damit er über allem liegt */}
      {gelesene && createPortal(
        <Suspense fallback={null}>
          <Gelesene onClose={() => setGelesene(false)} />
        </Suspense>,
        document.body,
      )}
      {einstellungen && createPortal(
        <Suspense fallback={null}>
          <Einstellungen onClose={() => setEinstellungen(false)} />
        </Suspense>,
        document.body,
      )}
    </div>
  );
}
